// ═════════════════════════════════════════════════════════════
// Responsive image helpers · WebP + multi-size srcSet
// ═════════════════════════════════════════════════════════════
//
// - `webpSrc(path)`  → best single-file WebP URL if we have one, else source
// - `srcSetFor(path, [widths])` → responsive srcSet string ("x-480w.webp 480w,
//                                  x-960w.webp 960w, ...") or empty if not
//                                  indexed
// - `bestSrcFor(path)` → fallback single URL (largest indexed WebP)
//
// Browser WebP support in 2026 is 99 %+ (Safari since iOS 14, Sep 2020).
// We do a direct src swap — no <picture> wrapper needed.

import RESPONSIVE_MANIFEST from "./responsiveImageManifest.json";

// Original single-size WebP manifest (files where converting the whole image
// to WebP produced a meaningfully smaller result — 63 entries).
const WEBP_MANIFEST = new Set([
  "/assets/amselweg/img-01-1920.jpg",
  "/assets/amselweg/img-04-thumb.jpg",
  "/assets/amselweg/img-04.jpg",
  "/assets/amselweg/img-05-thumb.jpg",
  "/assets/amselweg/img-05.jpg",
  "/assets/amselweg/img-06-thumb.jpg",
  "/assets/amselweg/img-06.jpg",
  "/assets/amselweg/img-07-thumb.jpg",
  "/assets/amselweg/img-07.jpg",
  "/assets/amselweg/img-10-thumb.jpg",
  "/assets/amselweg/img-10.jpg",
  "/assets/amselweg/img-14-thumb.jpg",
  "/assets/amselweg/img-14.jpg",
  "/assets/amselweg/img-15-thumb.jpg",
  "/assets/amselweg/img-15.jpg",
  "/assets/amselweg/img-16-thumb.jpg",
  "/assets/amselweg/img-16.jpg",
  "/assets/amselweg/img-18-thumb.jpg",
  "/assets/amselweg/img-18.jpg",
  "/assets/amselweg/img-19-thumb.jpg",
  "/assets/amselweg/img-19.jpg",
  "/assets/amselweg/img-20-thumb.jpg",
  "/assets/amselweg/img-21-thumb.jpg",
  "/assets/amselweg/img-21.jpg",
  "/assets/baumhaselring/img-01.jpg",
  "/assets/baumhaselring/img-02.jpg",
  "/assets/baumhaselring/img-03.jpg",
  "/assets/baumhaselring/img-04.jpg",
  "/assets/baumhaselring/img-05.jpg",
  "/assets/baumhaselring/img-06.jpg",
  "/assets/baumhaselring/img-07.jpg",
  "/assets/baumhaselring/img-08.jpg",
  "/assets/baumhaselring/img-09.jpg",
  "/assets/baumhaselring/img-10.jpg",
  "/assets/baumhaselring/img-11.jpg",
  "/assets/baumhaselring/img-12.jpg",
  "/assets/baumhaselring/img-13.jpg",
  "/assets/karls/aerial-construction.jpg",
  "/assets/karls/drone-1.jpg",
  "/assets/karls/drone-2.jpg",
  "/assets/karls/drone-3.jpg",
  "/assets/karls/drone-4.jpg",
  "/assets/karls/drone-5.jpg",
  "/assets/karls/drone-6.jpg",
  "/assets/karls/exterior-1.jpg",
  "/assets/karls/exterior-2.jpg",
  "/assets/karls/exterior-3.jpg",
  "/assets/karls/floorplan-floor-dg.jpg",
  "/assets/karls/floorplan-floor-hp.jpg",
  "/assets/karls/floorplan-overview.jpg",
  "/assets/karls/floorplan-unit-we3208.jpg",
  "/assets/karls/hero-architecture.jpg",
  "/assets/karls/interior-bathroom.jpg",
  "/assets/karls/interior-bedroom.jpg",
  "/assets/karls/interior-kitchen.jpg",
  "/assets/karls/interior-living-room.jpg",
  "/assets/karls/location-map.jpg",
  "/assets/karls/location-map.png",
  "/assets/karls/rendering-main.jpg",
  "/assets/karls/site-poi.jpg",
  "/assets/karls/site-poi.png",
]);

/** Returns the .webp URL if a single-file WebP exists on disk. */
export function webpSrc(src) {
  if (!src || typeof src !== "string") return src;
  if (!src.startsWith("/assets/")) return src;
  if (!WEBP_MANIFEST.has(src)) return src;
  return src.replace(/\.(jpe?g|png)$/i, ".webp");
}

/**
 * Normalize a src to the JPG/PNG lookup key used in the manifest.
 * Callers may already have gone through webpSrc() so `.webp` inputs are common.
 */
function normalizeKey(src) {
  if (src.endsWith(".webp")) {
    // Try both .jpg and .png fallbacks — the manifest is keyed on original
    const jpgKey = src.replace(/\.webp$/i, ".jpg");
    if (RESPONSIVE_MANIFEST[jpgKey]) return jpgKey;
    const pngKey = src.replace(/\.webp$/i, ".png");
    if (RESPONSIVE_MANIFEST[pngKey]) return pngKey;
  }
  return src;
}

/**
 * Returns a `srcSet` string of responsive WebP variants for a source path,
 * or empty string if this path isn't in the responsive manifest.
 */
export function srcSetFor(src) {
  if (!src || typeof src !== "string") return "";
  if (!src.startsWith("/assets/")) return "";
  const key = normalizeKey(src);
  const entry = RESPONSIVE_MANIFEST[key];
  if (!entry || !entry.widths || entry.widths.length === 0) return "";
  const base = key.replace(/\.(jpe?g|png)$/i, "");
  return entry.widths.map((w) => `${base}-${w}w.webp ${w}w`).join(", ");
}

/**
 * Returns the best single-URL fallback for src attribute (largest
 * indexed WebP, or the WebP swap, or the source path unchanged).
 */
export function bestSrcFor(src) {
  if (!src || typeof src !== "string") return src;
  const key = normalizeKey(src);
  const entry = RESPONSIVE_MANIFEST[key];
  if (entry && entry.widths && entry.widths.length > 0) {
    const largest = Math.max(...entry.widths);
    const base = key.replace(/\.(jpe?g|png)$/i, "");
    return `${base}-${largest}w.webp`;
  }
  return webpSrc(src);
}

/**
 * Standard `sizes` presets. Pass one to `<img sizes={SIZES.hero}>`.
 * Tells the browser what CSS-pixel width the image will occupy at each
 * breakpoint so it can pick the right file from srcSet.
 */
export const SIZES = {
  hero: "100vw", // full-bleed hero image
  half: "(max-width: 640px) 100vw, 50vw", // 2-col layouts on desktop
  third: "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw",
  quarter: "(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw",
  gallery: "(max-width: 640px) 100vw, (max-width: 1024px) 66vw, 50vw",
};

export default webpSrc;
